import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

// Dynamically import images
const importAll = (r) =>
  r
    .keys()
    .map((key) => {
      const fileName = key
        .split("/")
        .pop()
        .replace(/\.[^/.]+$/, "");

      const parts = fileName.split("-").map((p) => p.trim());
      const order = parseInt(parts[0]) || 9999;
      const personName = parts[1] || "";
      const designation = parts[2] || "";
      const location = parts[3] || "";

      return { src: r(key), order, personName, designation, location };
    })
    .sort((a, b) => a.order - b.order);

// Imports
const patronsImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/Patrons",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const youthWingImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/Youth WIng",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const nationalCommitteeImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/National Leaders",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const socialJusticeWingImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/Social Justice Wing",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const lawJusticeWingImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/Law & Justice Wing",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const advisoryResearchCommitteeImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/Advisory & Researh Committee",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const medicalHealthWingImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/Madical & Health Wing",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const itWingImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/IT Wing",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);
const stateCommitteeImages = importAll(
  require.context(
    "../../../assets/Aboutus/Committee/State Committee",
    false,
    /\.(png|jpe?g|webp)$/,
  ),
);

const groupByLocation = (list) => {
  const groups = {};
  list.forEach((item) => {
    const loc = item.location || "National";
    if (!groups[loc]) groups[loc] = [];
    groups[loc].push(item);
  });
  return groups;
};

const patronsLocationGroups = groupByLocation(patronsImages);
const youthWingLocationGroups = groupByLocation(youthWingImages);
const nationalCommitteeLocationGroups = groupByLocation(
  nationalCommitteeImages,
);
const socialJusticeWingLocationGroups = groupByLocation(
  socialJusticeWingImages,
);
const lawJusticeWingLocationGroups = groupByLocation(lawJusticeWingImages);
const advisoryResearchCommitteeLocationGroups = groupByLocation(
  advisoryResearchCommitteeImages,
);
const medicalHealthWingLocationGroups = groupByLocation(
  medicalHealthWingImages,
);
const itWingLocationGroups = groupByLocation(itWingImages);
const stateCommitteeLocationGroups = groupByLocation(stateCommitteeImages);

// Committee Grid Section
const CommitteeSection = ({ locationGroups, sortedLocations }) => (
  <div className="mt-4">
    <div className="space-y-8">
      {sortedLocations.map((location, idx) => {
        const people = locationGroups[location];
        if (!people?.length) return null;

        return (
          <div
            key={idx}
            className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
          >
            {sortedLocations.length > 1 && (
              <div className="px-4 md:px-6 pt-4 pb-2 bg-gray-50">
                <h3 className="text-lg md:text-xl font-bold text-gray-800">
                  {location}
                </h3>
              </div>
            )}

            <div className="p-4 md:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                {people.map((person, i) => (
                  <div
                    key={i}
                    className="group h-full bg-white  rounded-2xl hover:border-blue-600 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    <div className="p-4 flex flex-col items-center">
                      <div className="relative mb-3">
                        <img
  src={person.src}
  alt={person.personName}
  className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 xl:w-40 xl:h-40 object-cover rounded-full border-2 border-gray-300 group-hover:border-blue-500 transition-all duration-300"
  loading="lazy"
/>

                      </div>

                      <div className="w-full flex flex-col items-center text-center">
                        <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-2">
                          {person.personName.toUpperCase()}
                        </h3>

                        {person.designation && (
                          <div className="text-blue-800 font-medium text-xs mb-1 line-clamp-2">
                            {person.designation.toUpperCase()}
                          </div>
                        )}

                        {person.location && (
                          <div className="text-gray-600 text-xs truncate">
                            {person.location}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Pyramid Layout (unchanged but centered)
const PyramidCommitteeSection = ({ locationGroups, sortedLocations }) => (
  <div className="mt-4">
    <div className="space-y-8">
      {sortedLocations.map((location, idx) => {
        const people = locationGroups[location];
        if (!people?.length) return null;

        const rows = [];
        let remaining = [...people];
        let i = 0;
        while (remaining.length) {
          const size = Math.min(i + 1, 5);
          rows.push(remaining.splice(0, size));
          i++;
        }

        return (
          <div
            key={idx}
            className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
          >
            {sortedLocations.length > 1 && (
              <div className="px-4 md:px-6 pt-4 pb-2 bg-gray-50">
                <h3 className="text-lg md:text-xl font-bold text-gray-800">
                  {location}
                </h3>
              </div>
            )}

            <div className="p-4 md:p-6 flex flex-col items-start space-y-6">
              {rows.map((row, ri) => (
                <div key={ri} className="flex flex-wrap justify-start gap-4">
                  {row.map((person, pi) => (
                    <div
                      key={pi}
                      className="group bg-white  rounded-2xl  p-4 flex flex-col items-center"
                    >
                    <img
  src={person.src}
  alt={person.personName}
  className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 xl:w-36 xl:h-36 object-cover rounded-full border-2 border-gray-300  mb-2"
/>

                      <h3 className="font-bold text-gray-900 text-xs mb-1 line-clamp-2 text-center">
                        {person.personName.toUpperCase()}
                      </h3>
                      {person.designation && (
                        <div className="text-blue-800 font-medium text-xs mb-1 line-clamp-2 text-center">
                          {person.designation.toUpperCase()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Tab Button
const CommitteeTab = ({ title, index, isActive, onClick }) => {
  const isBlueBg = index % 2 === 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 md:px-6 md:py-4 rounded-xl border-2 transition-all duration-300 flex justify-between items-center ${
        isActive
          ? "bg-blue-700 text-white border-blue-700 font-bold shadow-lg"
          : isBlueBg
            ? "bg-blue-700 text-white border-blue-600 hover:bg-blue-800 hover:shadow-md"
            : "bg-white text-black border-gray-200 hover:bg-gray-50 hover:shadow-md"
      }`}
    >
      <span className="text-sm md:text-base font-medium">{title}</span>
      {isActive ? <ChevronUp /> : <ChevronDown />}
    </button>
  );
};

export default function Patrons() {
  const [activeTabs, setActiveTabs] = useState(["patrons"]);
  const toggleTab = (id) =>
    setActiveTabs((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );

  const committees = [
    {
      id: "patrons",
      title: "Honorable National Patron Members",
      groups: patronsLocationGroups,
    },
    {
      id: "national",
      title: "National Committee",
      groups: nationalCommitteeLocationGroups,
      pyramid: true,
    },
    {
      id: "state",
      title: "State Committee",
      groups: stateCommitteeLocationGroups,
    },
   
    {
      id: "social",
      title: "Social Justice Wing",
      groups: socialJusticeWingLocationGroups,
    },
    {
      id: "law",
      title: "Law & Justice Wing",
      groups: lawJusticeWingLocationGroups,
    },
    {
      id: "advisory",
      title: "Advisory & Research Committee",
      groups: advisoryResearchCommitteeLocationGroups,
    },
    {
      id: "medical",
      title: "Medical & Health Wing",
      groups: medicalHealthWingLocationGroups,
    },
     { id: "it", title: "IT Wing", groups: itWingLocationGroups },
    {
      id: "youth",
      title: "Youth Wing",
      groups: youthWingLocationGroups,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-7xl mx-auto space-y-4">
        {committees.map((c, i) => {
          const sortedLocations = Object.keys(c.groups).sort();
          return (
            <div key={c.id}>
              <CommitteeTab
                title={c.title}
                index={i}
                isActive={activeTabs.includes(c.id)}
                onClick={() => toggleTab(c.id)}
              />
              {activeTabs.includes(c.id) && (
                <div className="animate-fadeIn px-2 md:px-4">
                  {c.pyramid ? (
                    <PyramidCommitteeSection
                      locationGroups={c.groups}
                      sortedLocations={sortedLocations}
                    />
                  ) : (
                    <CommitteeSection
                      locationGroups={c.groups}
                      sortedLocations={sortedLocations}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
